import { ArrowLeft, Bot, CircleDot } from "lucide-react";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";

import { PageContainer } from "@/components/PageContainer";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useAdminAiOps } from "@/hooks/useAdminAiOps";
import type { AiCallEntry } from "@/hooks/useAdminAiOps";
import { useMe } from "@/hooks/useMe";
import { isMaitimeStaff } from "@/lib/nav-config";
import { cn } from "@/lib/utils";

const MODULI = ["", "M1", "M2", "M3", "M4", "M5", "LIAISON", "system"];
const ESITI = ["", "success", "error", "timeout"];

function formatDataOra(iso: string): string {
  return new Date(iso).toLocaleString("it-IT", {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatCosto(s: string): string {
  const n = parseFloat(s);
  if (isNaN(n)) return "—";
  if (n === 0) return "$0.000";
  if (n < 0.001) return `$${n.toExponential(2)}`;
  return `$${n.toFixed(4)}`;
}

export function AdminAiOpsPage() {
  const { data: me, isLoading: loadingMe } = useMe();
  const [modulo, setModulo] = useState("");
  const [esito, setEsito] = useState("");
  const [giorni, setGiorni] = useState(7);

  const { data, isLoading } = useAdminAiOps({
    modulo: modulo || undefined,
    esito: esito || undefined,
    giorni,
    limit: 200,
  });

  if (!loadingMe && me && !isMaitimeStaff(me)) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageContainer>
      <Link
        to="/admin"
        className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors w-fit"
      >
        <ArrowLeft className="size-3.5" />
        Backoffice
      </Link>

      <header className="space-y-1">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Backoffice Maitime
        </p>
        <h1 className="text-3xl font-semibold tracking-tight text-foreground">
          AI Ops
        </h1>
        <p className="text-sm text-muted-foreground max-w-2xl pt-1">
          Tracking chiamate Anthropic per modulo. Cost monitoring + latenza + error rate. Foundation pronta — popolata appena il primo modulo AI inizia a chiamare (M2 in roadmap).
        </p>
      </header>

      <div className="rounded-lg border bg-card p-3 flex flex-wrap gap-3 items-end">
        <Field label="Modulo">
          <select
            value={modulo}
            onChange={(e) => setModulo(e.target.value)}
            className="rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {MODULI.map((m) => (
              <option key={m} value={m}>
                {m || "tutti"}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Esito">
          <select
            value={esito}
            onChange={(e) => setEsito(e.target.value)}
            className="rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {ESITI.map((e) => (
              <option key={e} value={e}>
                {e || "tutti"}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Periodo">
          <select
            value={giorni}
            onChange={(e) => setGiorni(parseInt(e.target.value, 10))}
            className="rounded-md border bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value={1}>Ultimo giorno</option>
            <option value={7}>Ultima settimana</option>
            <option value={30}>Ultimo mese</option>
            <option value={90}>Ultimi 3 mesi</option>
          </select>
        </Field>
      </div>

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : !data ? (
        <div className="rounded-lg border bg-destructive/10 p-4 text-sm text-destructive">
          Errore caricamento.
        </div>
      ) : data.totale_chiamate_periodo === 0 ? (
        <div className="rounded-lg border-2 border-dashed bg-card/40 p-12 text-center">
          <Bot className="size-10 mx-auto text-muted-foreground mb-3" />
          <p className="text-sm font-medium text-foreground">
            Nessuna chiamata AI nel periodo
          </p>
          <p className="text-xs text-muted-foreground mt-2 max-w-md mx-auto">
            La foundation AI Ops è pronta (tabella + wrapper + pricing). Si popolerà appena il primo modulo Anthropic inizierà a fare chiamate vere — previsto con l'attivazione di M2.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Pill
              label="Chiamate periodo"
              value={String(data.totale_chiamate_periodo)}
            />
            <Pill label="Costo totale USD" value={formatCosto(data.totale_costo_usd)} />
            <Pill label="Periodo" value={`${data.periodo_giorni} giorni`} />
          </div>

          <section>
            <h2 className="text-sm font-semibold text-foreground mb-2">
              Breakdown per modulo + esito
            </h2>
            <div className="rounded-lg border bg-card overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium px-3 py-2">Modulo</th>
                    <th className="text-left font-medium px-3 py-2">Esito</th>
                    <th className="text-right font-medium px-3 py-2">N</th>
                    <th className="text-right font-medium px-3 py-2">Token in</th>
                    <th className="text-right font-medium px-3 py-2">Token out</th>
                    <th className="text-right font-medium px-3 py-2">Latenza ms</th>
                    <th className="text-right font-medium px-3 py-2">Costo</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.breakdown.map((b, i) => (
                    <tr key={i}>
                      <td className="px-3 py-2">
                        <Badge variant="outline" className="text-[10px]">
                          {b.modulo}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        <EsitoBadge esito={b.esito} />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{b.n}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {b.token_input.toLocaleString("it-IT")}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {b.token_output.toLocaleString("it-IT")}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {b.latenza_media_ms}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {formatCosto(b.costo_totale_usd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-foreground mb-2">
              Chiamate (ultime {data.entries.length})
            </h2>
            <div className="rounded-lg border bg-card overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-muted-foreground">
                  <tr>
                    <th className="text-left font-medium px-3 py-2 w-[120px]">Quando</th>
                    <th className="text-left font-medium px-3 py-2 w-[70px]">Modulo</th>
                    <th className="text-left font-medium px-3 py-2">Agente</th>
                    <th className="text-left font-medium px-3 py-2 w-[140px]">Modello</th>
                    <th className="text-right font-medium px-3 py-2 w-[60px]">Lat.</th>
                    <th className="text-right font-medium px-3 py-2 w-[70px]">Tok.</th>
                    <th className="text-right font-medium px-3 py-2 w-[80px]">Costo</th>
                    <th className="text-left font-medium px-3 py-2 w-[80px]">Esito</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.entries.map((e) => (
                    <CallRow key={e.id} entry={e} />
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </PageContainer>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">
        {label}
      </label>
      {children}
    </div>
  );
}

function Pill({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border bg-card px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold text-foreground tabular-nums mt-0.5">
        {value}
      </div>
    </div>
  );
}

function EsitoBadge({ esito }: { esito: string }) {
  const colore =
    esito === "success"
      ? "bg-success/10 text-success border-success/30"
      : esito === "error"
        ? "bg-destructive/10 text-destructive border-destructive/30"
        : "bg-amber-100 text-amber-900 border-amber-300";
  return (
    <Badge variant="outline" className={cn("text-[10px]", colore)}>
      <CircleDot className="size-3 mr-1" />
      {esito}
    </Badge>
  );
}

function CallRow({ entry: e }: { entry: AiCallEntry }) {
  return (
    <tr className="hover:bg-muted/20">
      <td className="px-3 py-2 text-[11px] text-muted-foreground whitespace-nowrap tabular-nums">
        {formatDataOra(e.creato_il)}
      </td>
      <td className="px-3 py-2">
        <Badge variant="outline" className="text-[10px]">
          {e.modulo}
        </Badge>
      </td>
      <td className="px-3 py-2 text-[11px] font-mono text-foreground">
        {e.agente}
        {e.prompt_version && (
          <span className="text-muted-foreground"> · {e.prompt_version}</span>
        )}
      </td>
      <td className="px-3 py-2 text-[11px] font-mono text-muted-foreground">
        {e.modello}
      </td>
      <td className="px-3 py-2 text-right tabular-nums">{e.latenza_ms}</td>
      <td className="px-3 py-2 text-right tabular-nums text-[11px]">
        {e.token_input + e.token_output}
      </td>
      <td className="px-3 py-2 text-right tabular-nums font-medium">
        {formatCosto(e.costo_usd)}
      </td>
      <td className="px-3 py-2">
        <EsitoBadge esito={e.esito} />
      </td>
    </tr>
  );
}
